import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { LowerCasePipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { UiIllustrationComponent } from '../illustration/ui-illustration.component';

/**
 * refresh-parent-portal-ui 0.1: a dashboard tile — illustration in the cream
 * circle, a label, an optional red count badge. Navigates to `link`.
 */
@Component({
  selector: 'ui-tile',
  standalone: true,
  imports: [RouterLink, UiIllustrationComponent, LowerCasePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <a class="tile" [routerLink]="link()" [attr.data-testid]="('tile-' + label()) | lowercase">
      @if (badge()) {
        <span class="badge">{{ badge() }}</span>
      }
      <ui-illustration [name]="illustration()" [size]="36" />
      <span class="label">{{ label() }}</span>
    </a>
  `,
  styles: [
    `
      .tile {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        text-align: center;
        padding: 14px 8px 12px;
        border-radius: var(--gp-radius-card);
        border: 1.5px solid var(--gp-tone-border);
        background: var(--gp-tone-bg);
        text-decoration: none;
        font-weight: 800;
        font-size: 16px;
        line-height: 20px;
        color: var(--gp-tone-ink);
      }
      .badge {
        position: absolute;
        top: 8px;
        right: 10px;
        background: #e5533f;
        color: #fff;
        border-radius: 999px;
        min-width: 24px;
        height: 24px;
        display: grid;
        place-items: center;
        font-size: 13px;
        border: 2px solid #fff;
      }
      :host([data-tone='sage']) {
        --gp-tone-bg: var(--gp-tile-sage-bg);
        --gp-tone-border: var(--gp-tile-sage-border);
        --gp-tone-ink: var(--gp-tile-sage-ink);
      }
      :host([data-tone='mint']) {
        --gp-tone-bg: var(--gp-tile-mint-bg);
        --gp-tone-border: var(--gp-tile-mint-border);
        --gp-tone-ink: var(--gp-tile-mint-ink);
      }
      :host([data-tone='sky']) {
        --gp-tone-bg: var(--gp-tile-sky-bg);
        --gp-tone-border: var(--gp-tile-sky-border);
        --gp-tone-ink: var(--gp-tile-sky-ink);
      }
      :host([data-tone='butter']) {
        --gp-tone-bg: var(--gp-tile-butter-bg);
        --gp-tone-border: var(--gp-tile-butter-border);
        --gp-tone-ink: var(--gp-tile-butter-ink);
      }
      :host([data-tone='peach']) {
        --gp-tone-bg: var(--gp-tile-peach-bg);
        --gp-tone-border: var(--gp-tile-peach-border);
        --gp-tone-ink: var(--gp-tile-peach-ink);
      }
      :host([data-tone='lavender']) {
        --gp-tone-bg: var(--gp-tile-lavender-bg);
        --gp-tone-border: var(--gp-tile-lavender-border);
        --gp-tone-ink: var(--gp-tile-lavender-ink);
      }
    `,
  ],
  host: {
    '[attr.data-tone]': 'tone()',
  },
})
export class UiTileComponent {
  readonly label = input.required<string>();
  readonly illustration = input.required<string>();
  readonly tone = input<'sage' | 'mint' | 'sky' | 'butter' | 'peach' | 'lavender'>('mint');
  readonly link = input.required<unknown[] | string>();
  readonly badge = input<string | number>('');
}